import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Project } from '@/types/project';

interface HeaderTab {
  id: string;
  label: string;
  icon: React.ReactNode;
}

interface ProjectDetailHeaderDesktopProps {
  project: Project | null;
  loading: boolean;
  activeTab: string;
  tabs: HeaderTab[];
  showUnlocksTab: boolean;
  onTabChange: (tabId: string) => void;
  onPlaceInquiry: () => void;
}

const ProjectDetailHeaderDesktop: React.FC<ProjectDetailHeaderDesktopProps> = ({
  project,
  loading,
  activeTab,
  tabs,
  showUnlocksTab,
  onTabChange,
  onPlaceInquiry,
}) => {
  const [imgError, setImgError] = useState(false)

  useEffect(() => {
    setImgError(false)
  }, [project?.logo])

  return (
    <div className="bg-[rgba(255,255,255,0.32)] dark:bg-[rgba(23,23,23,0.72)] border border-white dark:border-[#171717] overflow-hidden">
      <div className="px-7 pt-6 pb-0">
        <div className="flex items-start justify-between mb-6">
          <div className="flex-1 max-w-[894px]">
            <div className="flex items-center gap-[10px] mb-[6px]">
              <div className="w-7 h-7 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-800 flex-shrink-0 flex items-center justify-center">
                {project?.logo && !imgError ? (
                  <img
                    src={project.logo}
                    alt={project.project_name}
                    className="w-full h-full object-cover"
                    onError={() => setImgError(true)}
                  />
                ) : (
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    {(project?.project_name || 'P').charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <h1 className="font-['Plus_Jakarta_Sans'] font-semibold text-[23px] leading-[32px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
                {loading ? 'Loading...' : project?.project_name || 'Project'}
              </h1>
            </div>
            {project?.description && (
              <p className="font-['Plus_Jakarta_Sans'] font-normal text-[16px] leading-[24px] text-[#2d2923] dark:text-[rgba(253,253,253,0.8)]">
                {project.description}
              </p>
            )}
          </div>

          {!loading && project && (
            <div className="flex items-center gap-[6px] flex-shrink-0 ml-4">
              <Button
                onClick={onPlaceInquiry}
                className="h-8 bg-[#2d2923] hover:bg-[rgba(45,41,35,0.92)] dark:bg-white dark:hover:bg-[rgba(253,253,253,0.88)] text-white dark:text-[#2d2923] font-['Plus_Jakarta_Sans'] font-medium text-[13px] leading-[18px] px-[18px] py-0 rounded-none"
              >
                Place Inquiry
              </Button>
              {project.link && (
                <Button
                  variant="outline"
                  className="h-8 bg-white dark:bg-[#2d2923] border-[rgba(45,41,35,0.12)] dark:border-[#2d2923] text-[#2d2923] dark:text-white font-['Plus_Jakarta_Sans'] font-medium text-[13px] leading-[18px] px-[18px] py-0 rounded-none"
                  asChild
                >
                  <a
                    href={project.link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Visit Website
                  </a>
                </Button>
              )}
            </div>
          )}
        </div>

        <div className="flex items-end">
          {tabs
            .filter(tab => (tab.id === 'unlocks' ? showUnlocksTab : true))
            .map((tab) => (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                className={`
                  flex items-center gap-[4px] px-4 py-[12px] relative
                  font-['Plus_Jakarta_Sans'] font-medium text-[12px] leading-[16px]
                  transition-colors
                  ${activeTab === tab.id
                    ? 'text-[#2d2923] dark:text-[#fdfdfd]'
                    : 'text-[rgba(45,41,35,0.8)] dark:text-[rgba(253,253,253,0.8)] hover:text-[#2d2923] dark:hover:text-[#fdfdfd]'
                  }
                `}
              >
                <span className={activeTab === tab.id ? 'text-[#2d2923] dark:text-[#fdfdfd]' : 'text-[rgba(45,41,35,0.8)] dark:text-[rgba(253,253,253,0.8)]'}>
                  {tab.icon}
                </span>
                <span>{tab.label}</span>
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-[#2d2923] dark:bg-[#fdfdfd] rounded-t-[2px]" />
                )}
              </button>
            ))}
        </div>
      </div>
    </div>
  );
};

export default ProjectDetailHeaderDesktop;
